import React from 'react'
import ReportSubtitle from 'components/Text/ReportSubtitle'
import ReportSubtitleV1 from 'components/Text/ReportSubtitleV1'
import useLanguage from 'features/languageReport/useLanguage'
import {Lobe} from 'api/analysisApi'
import useStyles from 'components/molcules/ProfitSummaryReport/useStyles'
import {ResEEG} from 'api/languageApi'
import useLanguageReport from 'hooks/useLanguageReport'

const scoreToString = (
  score: number | string,
  textEegAPI: ResEEG | null,
  percentile: {
    low: number
    high: number
  },
) => {
  const parsedScore =
    typeof score === 'string' ? Number.parseFloat(score) : score
  if (parsedScore < percentile.low) {
    return textEegAPI?.brain_aging.status.low
  }
  if (parsedScore <= percentile.high) {
    return textEegAPI?.brain_aging.status.normal
  }

  return textEegAPI?.brain_aging.status.high
}

const scoreToClass = (
  score: number | string,
  percentile: {
    low: number
    high: number
  },
) => {
  const parsedScore =
    typeof score === 'string' ? Number.parseFloat(score) : score

  if (parsedScore < percentile.low) {
    return 'Underactive'
  }
  if (parsedScore <= percentile.high) {
    return 'Balanced'
  }

  return 'OverActive'
}

const Score = ({score}: {score: number | string}) => {
  const {textHealthAPI} = useLanguage('health')
  const {textEegAPI, percentile} = useLanguage('eeg')
  const string = scoreToString(score, textEegAPI, percentile)
  const className = scoreToClass(score, percentile)
  return (
    <div className='notranslate' style={{textAlign: 'center'}}>
      <div className='Score'>
        {score} {textHealthAPI?.common.score_unit.percentile}
      </div>
      <div className={`Label ${className}`}>{string}</div>
    </div>
  )
}

export default function EEGSummaryLobeAPI(props: {
  lobe: Lobe
  index: number
  isSummary?: boolean
}) {
  const {lobe, index, isSummary} = props
  useLanguageReport('health')
  const {textEegAPI} = useLanguage('eeg')
  const classes = useStyles()

  return (
    <div className={classes.eeg}>
      {isSummary ? (
        <ReportSubtitleV1 number={`${index}`}>
          {textEegAPI?.brain_aging?.basic?.section?.lobes?.title}
        </ReportSubtitleV1>
      ) : (
        <ReportSubtitle number={`${index}`}>
          {textEegAPI?.brain_aging?.basic?.section?.lobes?.title}
        </ReportSubtitle>
      )}

      <div className={classes.p_brainScore}>
        <div className='Brain-score-table'>
          <table>
            <thead>
              <tr>
                <th style={{textAlign: 'left', paddingLeft: 5}}>
                  {textEegAPI?.brain_aging?.basic?.section?.lobes?.score_table
                    .headers[0] || ''}
                </th>
                <th style={{textAlign: 'left'}}>
                  {textEegAPI?.brain_aging.basic.section.lobes.score_table
                    .headers[1] || ''}
                </th>
                <th>
                  {textEegAPI?.brain_aging.basic.section.lobes.score_table
                    .headers[2] || ''}
                </th>
                <th>
                  {textEegAPI?.brain_aging.basic.section.lobes.score_table
                    .headers[3] || ''}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className='AreaLabel alignMiddle' style={{width: '10vh'}}>
                  {textEegAPI?.brain_aging.basic.section.lobes.score_table
                    .rows[0].lobe || ''}
                </td>
                <td className='AreaLabel alignMiddle' style={{width: '35vh'}}>
                  {textEegAPI?.brain_aging.basic.section.lobes.score_table
                    .rows[0].functions.total || ''}
                </td>
                <td>
                  <Score score={lobe.Frontal_LH} />
                </td>
                <td>
                  <Score score={lobe.Frontal_RH} />
                </td>
              </tr>
              <tr>
                <td className='AreaLabel alignMiddle'>
                  {textEegAPI?.brain_aging.basic.section.lobes.score_table
                    .rows[1].lobe || ''}
                </td>
                <td className='AreaLabel alignMiddle'>
                  {textEegAPI?.brain_aging.basic.section.lobes.score_table
                    .rows[1].functions.total || ''}
                </td>
                <td>
                  <Score score={lobe.Temporal_LH} />
                </td>
                <td>
                  <Score score={lobe.Temporal_RH} />
                </td>
              </tr>
              <tr>
                <td className='AreaLabel alignMiddle'>
                  {textEegAPI?.brain_aging.basic.section.lobes.score_table
                    .rows[2].lobe || ''}
                </td>
                <td className='AreaLabel alignMiddle'>
                  {textEegAPI?.brain_aging.basic.section.lobes.score_table
                    .rows[2].functions.total || ''}
                </td>
                <td>
                  <Score score={lobe.Parietal_LH} />
                </td>
                <td>
                  <Score score={lobe.Parietal_RH} />
                </td>
              </tr>
              <tr>
                <td className='AreaLabel alignMiddle'>
                  {textEegAPI?.brain_aging.basic.section.lobes.score_table
                    .rows[3].lobe || ''}
                </td>
                <td className='AreaLabel alignMiddle'>
                  {textEegAPI?.brain_aging.basic.section.lobes.score_table
                    .rows[3].functions.total || ''}
                </td>
                <td>
                  <Score score={lobe.Occipital_LH} />
                </td>
                <td>
                  <Score score={lobe.Occipital_RH} />
                </td>
              </tr>
            </tbody>
          </table>

          <span className='Comment'>
            {textEegAPI?.brain_aging.basic.section.lobes.score_table.annotation}
          </span>
        </div>
        <div className='Brain-score-info'>
          <div className='Title'>
            {textEegAPI?.brain_aging.basic.section.lobes.description.main}
          </div>
          <div className='Desc'>
            {textEegAPI?.brain_aging.basic.section.lobes.description.sub[0]}
            <br />
            {textEegAPI?.brain_aging.basic.section.lobes.description.sub[1]}
          </div>
        </div>
      </div>
    </div>
  )
}
